import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { InfoRow } from '@app/shared/ui/molecules/Common';
import { CopyableInfoRow } from '@app/shared/ui/molecules/Admin/WithdrawalDetails';
import { UserAvatar, Button, Badge } from '@app/shared/ui/atoms/Common';
import { formatBalance, formatFrozenBalance } from '@app/shared/utils/common';
import { PriceType } from '@app/shared/types/common';
import { Gender } from '@app/shared/types/common';
import { Page } from '@app/routes';

interface UserInfoSectionProps {
  user: {
    id: number;
    fullName?: string | null;
    telegramUsername?: string | null;
    telegramId?: string | null;
    avatar?: string | null;
    gender: Gender;
    cookieBalance?: string;
    tonBalance?: string;
    tonFrozenBalance?: string;
  };
  curator: {
    id?: number;
    fullName?: string | null;
    telegramUsername?: string | null;
    telegramId?: string | null;
  };
}

export function UserInfoSection({ user, curator }: UserInfoSectionProps) {
  const { t } = useTranslation();

  const userTelegramUsername = user.telegramUsername || '';
  const userCookieBalance = user.cookieBalance;
  const userTonBalance = user.tonBalance;
  const userTonFrozenBalance = user.tonFrozenBalance;
  const curatorTelegramUsername = curator.telegramUsername || '';

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col">
        <div className="grid grid-cols-[1fr_96px] gap-4">
          <div className="flex flex-col">
            <InfoRow label="ID" value={<b>{user.id}</b>} />
            <InfoRow label={t('common.name')} value={<b>{user.fullName}</b>} />
          </div>
          <UserAvatar avatar={user.avatar} gender={user.gender} className="size-[96px]" />
        </div>

        <CopyableInfoRow
          label="Telegram"
          valueToDisplay={userTelegramUsername}
          valueToCopy={`@${userTelegramUsername}`}
        />
        <InfoRow label="Telegram ID" value={<b>{user.telegramId}</b>} />
        <InfoRow
          label={t('common.balance')}
          value={
            <div className="flex gap-2 text-center">
              {userCookieBalance && (
                <Badge>{formatBalance(PriceType.COOKIE, userCookieBalance)}</Badge>
              )}
              {userTonBalance && <Badge>{formatBalance(PriceType.TON, userTonBalance)}</Badge>}
              {userTonFrozenBalance && <Badge>{formatFrozenBalance(userTonFrozenBalance)}</Badge>}
            </div>
          }
          withBottomBorder={false}
        />
      </div>

      <div className="flex flex-col">
        <div className="grid grid-cols-[1fr_max-content] items-center gap-4">
          <span className="text-sm font-medium">{t('common.curator')}</span>
          <Button variant="secondary" size="sm" className="flex w-fit items-center px-2.5" asChild>
            <Link to={`${Page.ADMIN_USERS}/${curator.id}`}>
              {t('admin_user_details.curator_info')}
            </Link>
          </Button>
        </div>

        <InfoRow label="ID" value={<b>{curator.id}</b>} />
        <InfoRow label={t('common.name')} value={<b>{curator.fullName}</b>} />
        <CopyableInfoRow
          label="Telegram"
          valueToDisplay={curatorTelegramUsername}
          valueToCopy={`@${curatorTelegramUsername}`}
        />
        <InfoRow label="Telegram ID" value={<b>{curator.telegramId}</b>} withBottomBorder={false} />
      </div>
    </section>
  );
}
